import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { ViToastService } from '@vi-iot/ui';

import {
  BffInstallationsApi,
  EquipmentInstallationsResponse,
  GeneralManagementApi,
  GeneralManagementApiPicker,
  VitoguideBackendSettingsApiPicker,
} from '@vi-iot/client';

import { InstallationsActions } from './installations.actions';
import { InstallationsStore } from './installations.store';
import { UIService } from '@services';

@Injectable()
export class InstallationsEffects {
  listFirstThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.listFirstThousandInstallations),
      switchMap(() =>
        this.bffInstallationsApi.listInstallations().pipe(
          map((response) =>
            InstallationsActions.listFirstThousandInstallationsSuccess({
              installations: response.installations,
              cursor: response.cursor,
            })
          ),
          catchError((error) =>
            of(InstallationsActions.listFirstThousandInstallationsError({ error }))
          )
        )
      )
    );
  });

  listSecondThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.listFirstThousandInstallationsSuccess),
      switchMap((action) => {
        if (action.cursor) {
          return this.bffInstallationsApi.listInstallations({ cursor: action.cursor }).pipe(
            map((response) =>
              InstallationsActions.listSecondThousandInstallationsSuccess({
                installations: response.installations,
                cursor: response.cursor,
              })
            ),
            catchError((error) =>
              of(InstallationsActions.listSecondThousandInstallationsError({ error }))
            )
          );
        } else {
          return of(
            InstallationsActions.listSecondThousandInstallationsSuccess({ installations: [] })
          );
        }
      })
    );
  });

  listThirdThousandInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.listSecondThousandInstallationsSuccess),
      switchMap((action) => {
        if (action.cursor) {
          return this.bffInstallationsApi.listInstallations({ cursor: action.cursor }).pipe(
            map((response) =>
              InstallationsActions.listThirdThousandInstallationsSuccess({
                installations: response.installations,
              })
            ),
            catchError((error) =>
              of(InstallationsActions.listThirdThousandInstallationsError({ error }))
            )
          );
        } else {
          return of(
            InstallationsActions.listThirdThousandInstallationsSuccess({ installations: [] })
          );
        }
      })
    );
  });

  listLockedInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InstallationsActions.listLockedInstallations,
        InstallationsActions.listSecondThousandInstallationsError,
        InstallationsActions.listThirdThousandInstallationsError,
        InstallationsActions.listThirdThousandInstallationsSuccess
      ),
      switchMap(() =>
        this.generalManagementApiPicker
          .instance()
          .getInstallationsCollection({ onlyWithLockedDevices: true, includeGateways: true })
          .pipe(
            map((response: EquipmentInstallationsResponse) => {
              if (response.data == null) {
                response.data = [];
              }

              return InstallationsActions.listLockedInstallationsSuccess({
                lockedInstallations: response.data,
              });
            }),
            catchError((error) => of(InstallationsActions.listLockedInstallationsError({ error })))
          )
      )
    );
  });

  deregisterInstallation$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.deregisterInstallation),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.generalManagementApi.deleteInstallation(action.installationId).pipe(
          map(() => {
            this.uiService.unblockUI();

            return InstallationsActions.deregisterInstallationSuccess({
              installationId: action.installationId,
            });
          }),
          catchError(() => {
            this.uiService.unblockUI();

            return of(InstallationsActions.deregisterInstallationError());
          })
        )
      )
    );
  });

  fetchNumberOfDisplayedInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.fetchNumberOfDisplayedInstallations.attempt),
      concatLatestFrom(() => this.installationsStore.numberOfDisplayedInstallations$),
      filter(([_action, numberOfDisplayedInstallations]) => !numberOfDisplayedInstallations),
      switchMap(() =>
        this.vitoguideBackendSettingsApiPicker
          .instance()
          .getNumberOfDisplayedInstallations()
          .pipe(
            map(({ numberOfInstallations }) =>
              InstallationsActions.fetchNumberOfDisplayedInstallations.success({
                numberOfInstallationsToDisplay: numberOfInstallations,
              })
            ),
            catchError(() => of(InstallationsActions.fetchNumberOfDisplayedInstallations.failed()))
          )
      )
    );
  });

  setNumberOfDisplayedInstallations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationsActions.setNumberOfDisplayedInstallations.attempt),
      concatLatestFrom(() => this.installationsStore.numberOfDisplayedInstallations$),
      switchMap(([action, previousNumberOfInstallationsToDisplay]) =>
        this.vitoguideBackendSettingsApiPicker
          .instance()
          .setNumberOfDisplayedInstallations(action.numberOfInstallationsToDisplay)
          .pipe(
            map(() =>
              InstallationsActions.setNumberOfDisplayedInstallations.success({
                numberOfInstallationsToDisplay: action.numberOfInstallationsToDisplay,
              })
            ),
            catchError(() => {
              this.viToastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATIONS.PAGE_SIZE.ERROR'
                ),
              });

              return of(
                InstallationsActions.setNumberOfDisplayedInstallations.failed({
                  previousNumberOfInstallationsToDisplay,
                })
              );
            })
          )
      )
    );
  });

  constructor(
    private bffInstallationsApi: BffInstallationsApi,
    private generalManagementApi: GeneralManagementApi,
    private actions$: Actions,
    private generalManagementApiPicker: GeneralManagementApiPicker,
    private vitoguideBackendSettingsApiPicker: VitoguideBackendSettingsApiPicker,
    private installationsStore: InstallationsStore,
    private viToastService: ViToastService,
    private translateService: TranslateService,
    private uiService: UIService
  ) {}
}
